import type { MockDatabase } from './schema'

const now = new Date()
const minusDays = (days: number) => {
  const date = new Date(now)
  date.setDate(date.getDate() - days)
  return date.toISOString()
}

export const createMockSeed = (): MockDatabase => ({
  account: {
    profile: {
      id: 1,
      firstName: 'Marcel',
      lastName: 'Lefter',
      email: 'marcel@skalx.com',
      phone: '+373 69 000 000',
      timezone: 'Europe/Chisinau',
      title: 'Business Account Owner'
    },
    connectedAccounts: [
      {
        id: 'ca_password',
        provider: 'password',
        email: 'marcel@skalx.com',
        linkedAt: minusDays(420),
        status: 'active'
      },
      {
        id: 'ca_google',
        provider: 'google',
        email: 'marcel@skalx.com',
        linkedAt: minusDays(210),
        status: 'active'
      }
    ],
    security: {
      enforceMfa: false,
      allowPasswordLogin: true,
      allowSocialLogin: true,
      refreshTokenLifetimeDays: 30,
      trustedDeviceDays: 30
    },
    sessions: [
      {
        id: 'session_1',
        device: 'MacBook Pro',
        browser: 'Chrome 134',
        os: 'macOS',
        ipAddress: '109.185.22.2',
        location: 'Chisinau, MD',
        createdAt: minusDays(10),
        lastActiveAt: minusDays(0),
        current: true,
        status: 'active'
      },
      {
        id: 'session_2',
        device: 'iPhone 15',
        browser: 'Mobile Safari',
        os: 'iOS',
        ipAddress: '109.185.22.3',
        location: 'Chisinau, MD',
        createdAt: minusDays(24),
        lastActiveAt: minusDays(2),
        current: false,
        status: 'active'
      }
    ],
    passkeys: [
      {
        id: 1,
        name: 'MacBook Touch ID',
        credential_id_last_four: 'a3f9',
        created_at: minusDays(60),
        last_used_at: minusDays(0),
        transports: ['internal', 'hybrid']
      }
    ],
    recoveryCodes: {
      remainingCount: 5,
      lastGeneratedAt: minusDays(14)
    },
    trustedDevices: [
      {
        id: 'td_current_chrome',
        label: 'Work laptop',
        browser: 'Chrome 134',
        os: 'macOS',
        ipHint: '109.185.22.x',
        trustedAt: minusDays(10),
        lastUsedAt: minusDays(0),
        current: true
      },
      {
        id: 'td_phone_safari',
        label: 'Personal phone',
        browser: 'Mobile Safari',
        os: 'iOS',
        ipHint: '109.185.22.x',
        trustedAt: minusDays(24),
        lastUsedAt: minusDays(2),
        current: false
      }
    ]
  },
  workspaces: [
    {
      id: 101,
      name: 'Skalx Core',
      slug: 'skalx-core',
      domain: 'core',
      usersCount: 12,
      plan: 'growth',
      status: 'active',
      createdAt: minusDays(320),
      description: 'Main workspace for platform operations.'
    },
    {
      id: 102,
      name: 'Skalx Labs',
      slug: 'skalx-labs',
      domain: 'labs',
      usersCount: 6,
      plan: 'starter',
      status: 'active',
      createdAt: minusDays(124),
      description: 'R&D workspace for experimental apps.'
    },
    {
      id: 103,
      name: 'MediaLex',
      slug: 'medialex',
      domain: 'medialex',
      usersCount: 8,
      plan: 'growth',
      status: 'active',
      createdAt: minusDays(95),
      description: 'Workspace for media review and compliance workflows.'
    }
  ],
  roles: [
    {
      id: 'ws_role_owner',
      workspaceId: 101,
      name: 'owner',
      description: 'Full access to workspace and apps.',
      permissions: ['workspace.settings', 'workspace.members', 'workspace.roles', 'workspace.security', 'workspace.audit', 'apps.manage', 'apps.roles', 'apps.credentials'],
      system: true
    },
    {
      id: 'ws_role_admin',
      workspaceId: 101,
      name: 'admin',
      description: 'Admin access without destructive billing actions.',
      permissions: ['workspace.settings', 'workspace.members', 'workspace.roles', 'workspace.security', 'apps.manage', 'apps.roles', 'apps.credentials'],
      system: true
    },
    {
      id: 'ws_role_member',
      workspaceId: 101,
      name: 'member',
      description: 'Can access apps assigned by admins.',
      permissions: ['apps.manage'],
      system: true
    },
    {
      id: 'ws_role_owner_labs',
      workspaceId: 102,
      name: 'owner',
      description: 'Full access to workspace and apps.',
      permissions: ['workspace.settings', 'workspace.members', 'workspace.roles', 'workspace.security', 'workspace.audit', 'apps.manage', 'apps.roles', 'apps.credentials'],
      system: true
    },
    {
      id: 'ws_role_member_labs',
      workspaceId: 102,
      name: 'member',
      description: 'Can access apps assigned by admins.',
      permissions: ['apps.manage'],
      system: true
    },
    {
      id: 'ws_role_103_owner',
      workspaceId: 103,
      name: 'owner',
      description: 'Full access to workspace and apps.',
      permissions: [
        'workspace.settings',
        'workspace.members',
        'workspace.roles',
        'workspace.security',
        'workspace.audit',
        'apps.manage',
        'apps.roles',
        'apps.credentials'
      ],
      system: true
    },
    {
      id: 'ws_role_103_member',
      workspaceId: 103,
      name: 'member',
      description: 'Can access apps assigned by admins.',
      permissions: ['apps.manage'],
      system: true
    }
  ],
  members: [
    { id: 'm_1', workspaceId: 101, name: 'Marcel Lefter', email: 'marcel@skalx.com', roleId: 'ws_role_owner', status: 'active', joinedAt: minusDays(320), lastActiveAt: minusDays(0) },
    { id: 'm_2', workspaceId: 101, name: 'Ana Moraru', email: 'ana@skalx.com', roleId: 'ws_role_admin', status: 'active', joinedAt: minusDays(200), lastActiveAt: minusDays(1) },
    { id: 'm_3', workspaceId: 101, name: 'Vitalie Popa', email: 'vitalie@skalx.com', roleId: 'ws_role_member', status: 'invited', joinedAt: minusDays(2) },
    { id: 'm_4', workspaceId: 102, name: 'Marcel Lefter', email: 'marcel@skalx.com', roleId: 'ws_role_owner_labs', status: 'active', joinedAt: minusDays(124), lastActiveAt: minusDays(0) },
    {
      id: 'm_5',
      workspaceId: 103,
      name: 'Marcel Lefter',
      email: 'marcel@skalx.com',
      roleId: 'ws_role_103_owner',
      status: 'active',
      joinedAt: minusDays(95),
      lastActiveAt: minusDays(0)
    },
    {
      id: 'm_6',
      workspaceId: 103,
      name: 'Elena Costa',
      email: 'elena@medialex.mock',
      roleId: 'ws_role_103_member',
      status: 'active',
      joinedAt: minusDays(40),
      lastActiveAt: minusDays(1)
    },
    {
      id: 'm_7',
      workspaceId: 103,
      name: 'Jordan Lee',
      email: 'jordan@partner-media.org',
      roleId: 'ws_role_103_member',
      status: 'invited',
      joinedAt: minusDays(3)
    }
  ],
  securityPolicies: [
    { workspaceId: 101, authMethods: ['password', 'google', 'sso'], mfaPolicy: 'required_for_admins', invitePolicy: 'domain_restricted', jitProvisioning: true, defaultRoleId: 'ws_role_member' },
    { workspaceId: 102, authMethods: ['password', 'google'], mfaPolicy: 'optional', invitePolicy: 'invite_only', jitProvisioning: false, defaultRoleId: 'ws_role_member_labs' },
    {
      workspaceId: 103,
      authMethods: ['password', 'google', 'sso'],
      mfaPolicy: 'required_for_admins',
      invitePolicy: 'domain_restricted',
      jitProvisioning: true,
      defaultRoleId: 'ws_role_103_member'
    }
  ],
  ssoConnections: [
    {
      id: 'sso_1',
      workspaceId: 101,
      name: 'Okta Workforce',
      provider: 'Okta',
      protocol: 'saml',
      domain: 'skalx.com',
      status: 'active',
      lastCertificateRotationAt: minusDays(30)
    },
    {
      id: 'sso_2',
      workspaceId: 102,
      name: 'Azure AD (Labs)',
      provider: 'Microsoft Entra ID',
      protocol: 'oidc',
      domain: 'labs.skalx.com',
      status: 'active',
      lastCertificateRotationAt: minusDays(60)
    },
    {
      id: 'sso_3',
      workspaceId: 103,
      name: 'MediaLex Google Workspace',
      provider: 'Google',
      protocol: 'saml',
      domain: 'medialex.com',
      status: 'active',
      lastCertificateRotationAt: minusDays(14)
    },
    {
      id: 'sso_4',
      workspaceId: 101,
      name: 'JumpCloud SSO',
      provider: 'JumpCloud',
      protocol: 'oidc',
      domain: 'skalx.io',
      status: 'inactive',
      lastCertificateRotationAt: minusDays(90)
    }
  ],
  scimConnections: [
    {
      id: 'scim_1',
      workspaceId: 101,
      provider: 'Okta',
      status: 'active',
      endpointUrl: 'https://api.skalx.mock/scim/v2',
      bearerTokenLastFour: 'A92D',
      groupRoleMappingEnabled: true,
      lastSyncAt: minusDays(1)
    },
    {
      id: 'scim_1b',
      workspaceId: 101,
      provider: 'OneLogin',
      status: 'active',
      endpointUrl: 'https://skalx.onelogin.com/scim/v2',
      bearerTokenLastFour: 'F901',
      groupRoleMappingEnabled: false,
      lastSyncAt: minusDays(3)
    },
    {
      id: 'scim_2',
      workspaceId: 102,
      provider: 'Azure',
      status: 'inactive',
      endpointUrl: 'https://scim.labs.skalx.mock/v2',
      bearerTokenLastFour: 'B41C',
      groupRoleMappingEnabled: false,
      lastSyncAt: minusDays(45)
    },
    {
      id: 'scim_3',
      workspaceId: 103,
      provider: 'Google',
      status: 'active',
      endpointUrl: 'https://admin.google.com/medialex.mock/scim',
      bearerTokenLastFour: 'C88E',
      groupRoleMappingEnabled: true,
      lastSyncAt: minusDays(2)
    }
  ],
  domainRules: [
    { id: 'domain_1', workspaceId: 101, domain: 'skalx.com', mode: 'claim_required', verified: true },
    {
      id: 'domain_1b',
      workspaceId: 101,
      domain: 'skalx.io',
      mode: 'allow_invite',
      verified: true
    },
    {
      id: 'domain_1c',
      workspaceId: 101,
      domain: 'staging.skalx.com',
      mode: 'auto_join',
      verified: false
    },
    { id: 'domain_2', workspaceId: 102, domain: 'labs.skalx.com', mode: 'allow_invite', verified: false },
    {
      id: 'domain_2b',
      workspaceId: 102,
      domain: 'research.skalx.com',
      mode: 'claim_required',
      verified: true
    },
    {
      id: 'domain_3',
      workspaceId: 103,
      domain: 'medialex.com',
      mode: 'claim_required',
      verified: true
    },
    {
      id: 'domain_3b',
      workspaceId: 103,
      domain: 'partner-media.org',
      mode: 'allow_invite',
      verified: true
    },
    {
      id: 'domain_3c',
      workspaceId: 103,
      domain: 'contractors.medialex.com',
      mode: 'allow_invite',
      verified: false
    }
  ],
  apps: [
    { id: 'app_1', workspaceId: 101, name: 'Payments Console', slug: 'payments-console', goal: 'Centralized billing operations', description: 'Internal console for invoices, plans, and subscriptions.', owner: 'Ana Moraru', environment: 'production', status: 'active', createdAt: minusDays(140) },
    { id: 'app_2', workspaceId: 101, name: 'Identity Gateway', slug: 'identity-gateway', goal: 'SSO and API token broker', description: 'Auth gateway for partner integrations.', owner: 'Marcel Lefter', environment: 'staging', status: 'active', createdAt: minusDays(58) },
    {
      id: 'app_3',
      workspaceId: 102,
      name: 'Labs Playground',
      slug: 'labs-playground',
      goal: 'Experiment with product hypotheses',
      description: 'Sandbox application for rapid prototyping.',
      owner: 'Marcel Lefter',
      environment: 'development',
      status: 'active',
      createdAt: minusDays(41)
    },
    {
      id: 'app_4',
      workspaceId: 103,
      name: 'Review Desk',
      slug: 'review-desk',
      goal: 'Editorial review and legal hold',
      description: 'Case management for media assets pending review.',
      owner: 'Elena Costa',
      environment: 'production',
      status: 'active',
      createdAt: minusDays(50)
    },
    {
      id: 'app_5',
      workspaceId: 103,
      name: 'Compliance Reporter',
      slug: 'compliance-reporter',
      goal: 'Audit exports for regulators',
      description: 'Scheduled reports and evidence bundles.',
      owner: 'Marcel Lefter',
      environment: 'staging',
      status: 'active',
      createdAt: minusDays(22)
    }
  ],
  appCustomPermissions: [
    {
      id: 'app_perm_pay_export',
      appId: 'app_1',
      codeName: 'invoices.export',
      displayName: 'Export invoices',
      description: 'Allows CSV/PDF export of invoice data.',
      createdAt: minusDays(100)
    },
    {
      id: 'app_perm_pay_refund',
      appId: 'app_1',
      codeName: 'payments.refund',
      displayName: 'Issue refunds',
      description: 'Create refund transactions.',
      createdAt: minusDays(100)
    },
    {
      id: 'app_perm_igw_tokens',
      appId: 'app_2',
      codeName: 'tokens.mint',
      displayName: 'Mint API tokens',
      description: 'Create short-lived partner tokens.',
      createdAt: minusDays(50)
    }
  ],
  appRoles: [
    {
      id: 'app_role_1',
      appId: 'app_1',
      name: 'app_admin',
      description: 'Manage app settings and credentials.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: ['app_perm_pay_export', 'app_perm_pay_refund'],
      system: true
    },
    {
      id: 'app_role_2',
      appId: 'app_1',
      name: 'app_member',
      description: 'Use app and view assigned resources.',
      permissions: ['apps.manage'],
      customPermissionIds: ['app_perm_pay_refund'],
      system: true
    },
    {
      id: 'app_role_igw_admin',
      appId: 'app_2',
      name: 'app_admin',
      description: 'Manage app settings and credentials.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: ['app_perm_igw_tokens'],
      system: true
    },
    {
      id: 'app_role_igw_member',
      appId: 'app_2',
      name: 'app_member',
      description: 'Use app and view assigned resources.',
      permissions: ['apps.manage'],
      customPermissionIds: [],
      system: true
    },
    {
      id: 'app_role_lab_admin',
      appId: 'app_3',
      name: 'app_admin',
      description: 'Manage app settings and credentials.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: [],
      system: true
    },
    {
      id: 'app_role_lab_member',
      appId: 'app_3',
      name: 'app_member',
      description: 'Use app and view assigned resources.',
      permissions: ['apps.manage'],
      customPermissionIds: [],
      system: true
    },
    {
      id: 'app_role_mx_rd_admin',
      appId: 'app_4',
      name: 'app_admin',
      description: 'Manage review workflows.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: [],
      system: true
    },
    {
      id: 'app_role_mx_rd_reviewer',
      appId: 'app_4',
      name: 'reviewer',
      description: 'Review and approve assets.',
      permissions: ['apps.manage'],
      customPermissionIds: [],
      system: false
    },
    {
      id: 'app_role_mx_cr_admin',
      appId: 'app_5',
      name: 'app_admin',
      description: 'Manage compliance exports.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: [],
      system: true
    },
    {
      id: 'app_role_mx_cr_member',
      appId: 'app_5',
      name: 'app_member',
      description: 'View reports.',
      permissions: ['apps.manage'],
      customPermissionIds: [],
      system: true
    }
  ],
  appWebhooks: [
    { id: 'wh_1', appId: 'app_1', name: 'Billing events', url: 'https://hooks.skalx.mock/billing', events: ['invoice.created', 'invoice.paid', 'subscription.updated'], secretPrefix: 'whsec_f21d', status: 'active' },
    { id: 'wh_2', appId: 'app_1', name: 'Subscription lifecycle', url: 'https://hooks.skalx.mock/subs', events: ['subscription.created', 'subscription.canceled', 'trial.expired'], secretPrefix: 'whsec_a10b', status: 'active' },
    { id: 'wh_3', appId: 'app_2', name: 'Auth events', url: 'https://hooks.skalx.mock/auth', events: ['login.success', 'login.failed', 'token.refreshed', 'user.blocked'], secretPrefix: 'whsec_c33d', status: 'active' },
    { id: 'wh_4', appId: 'app_2', name: 'SSO sync', url: 'https://hooks.skalx.mock/sso-sync', events: ['sso.user.created', 'sso.user.deleted', 'sso.group.updated'], secretPrefix: 'whsec_e55f', status: 'disabled' },
    { id: 'wh_5', appId: 'app_3', name: 'Playground events', url: 'https://hooks.skalx.mock/playground', events: ['app.deployed', 'app.crashed'], secretPrefix: 'whsec_dev_01', status: 'active' },
    {
      id: 'wh_6',
      appId: 'app_4',
      name: 'Review status',
      url: 'https://hooks.medialex.mock/review',
      events: ['review.submitted', 'review.approved', 'review.rejected'],
      secretPrefix: 'whsec_mx_rd',
      status: 'active'
    },
    {
      id: 'wh_7',
      appId: 'app_5',
      name: 'Compliance jobs',
      url: 'https://hooks.medialex.mock/compliance',
      events: ['report.generated', 'export.ready'],
      secretPrefix: 'whsec_mx_cr',
      status: 'active'
    }
  ],
  audit: [
    {
      id: 'audit_1',
      scope: 'workspace',
      workspaceId: 101,
      actor: 'Marcel Lefter',
      action: 'workspace.security.updated',
      target: 'Skalx Core',
      severity: 'medium',
      createdAt: minusDays(3)
    },
    {
      id: 'audit_2',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'Ana Moraru',
      action: 'app.credential.created',
      target: 'Payments Console',
      severity: 'high',
      createdAt: minusDays(2)
    },
    {
      id: 'audit_3',
      scope: 'account',
      actor: 'Marcel Lefter',
      action: 'account.session.revoked',
      target: 'iPhone 15',
      severity: 'low',
      createdAt: minusDays(1)
    },
    {
      id: 'audit_4',
      scope: 'workspace',
      workspaceId: 101,
      actor: 'Ana Moraru',
      action: 'workspace.domain-rule.created',
      target: 'skalx.io',
      severity: 'medium',
      createdAt: minusDays(5)
    },
    {
      id: 'audit_5',
      scope: 'workspace',
      workspaceId: 101,
      actor: 'Marcel Lefter',
      action: 'workspace.api-key.created',
      target: 'Data pipeline',
      severity: 'high',
      createdAt: minusDays(8)
    },
    {
      id: 'audit_6',
      scope: 'workspace',
      workspaceId: 101,
      actor: 'system',
      action: 'workspace.sso.certificate-rotated',
      target: 'Okta Workforce',
      severity: 'medium',
      createdAt: minusDays(30)
    },
    {
      id: 'audit_7',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_2',
      actor: 'Marcel Lefter',
      action: 'app.webhook.updated',
      target: 'Auth events',
      severity: 'low',
      createdAt: minusDays(4)
    },
    {
      id: 'audit_8',
      scope: 'workspace',
      workspaceId: 102,
      actor: 'Marcel Lefter',
      action: 'workspace.sso.connection-created',
      target: 'Azure AD (Labs)',
      severity: 'high',
      createdAt: minusDays(12)
    },
    {
      id: 'audit_9',
      scope: 'workspace',
      workspaceId: 102,
      actor: 'Marcel Lefter',
      action: 'workspace.api-key.created',
      target: 'Labs experiments',
      severity: 'high',
      createdAt: minusDays(30)
    },
    {
      id: 'audit_10',
      scope: 'app',
      workspaceId: 102,
      appId: 'app_3',
      actor: 'Marcel Lefter',
      action: 'app.created',
      target: 'Labs Playground',
      severity: 'medium',
      createdAt: minusDays(41)
    },
    {
      id: 'audit_11',
      scope: 'workspace',
      workspaceId: 103,
      actor: 'Marcel Lefter',
      action: 'workspace.member.invited',
      target: 'jordan@partner-media.org',
      severity: 'low',
      createdAt: minusDays(3)
    },
    {
      id: 'audit_12',
      scope: 'workspace',
      workspaceId: 103,
      actor: 'Elena Costa',
      action: 'workspace.domain-rule.updated',
      target: 'contractors.medialex.com',
      severity: 'medium',
      createdAt: minusDays(6)
    },
    {
      id: 'audit_13',
      scope: 'workspace',
      workspaceId: 103,
      actor: 'Marcel Lefter',
      action: 'workspace.scim.sync',
      target: 'Google',
      severity: 'low',
      createdAt: minusDays(2)
    },
    {
      id: 'audit_14',
      scope: 'app',
      workspaceId: 103,
      appId: 'app_4',
      actor: 'Elena Costa',
      action: 'app.role.created',
      target: 'reviewer',
      severity: 'medium',
      createdAt: minusDays(10)
    },
    {
      id: 'audit_15',
      scope: 'app',
      workspaceId: 103,
      appId: 'app_5',
      actor: 'Marcel Lefter',
      action: 'app.credential.created',
      target: 'Reporting key',
      severity: 'high',
      createdAt: minusDays(20)
    },
    {
      id: 'audit_16',
      scope: 'app',
      workspaceId: 103,
      appId: 'app_4',
      actor: 'system',
      action: 'app.webhook.delivery-failed',
      target: 'Review status',
      severity: 'high',
      createdAt: minusDays(1)
    },
    {
      id: 'audit_17',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'Ana Moraru',
      action: 'app.webhook.created',
      target: 'Billing events',
      severity: 'medium',
      createdAt: minusDays(18)
    },
    {
      id: 'audit_18',
      scope: 'workspace',
      workspaceId: 103,
      actor: 'Marcel Lefter',
      action: 'workspace.api-key.revoked',
      target: 'Revoked legacy',
      severity: 'high',
      createdAt: minusDays(60)
    },
    {
      id: 'audit_app1_1',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'Ana Moraru',
      action: 'app.updated',
      target: 'Payments Console',
      severity: 'low',
      createdAt: minusDays(25)
    },
    {
      id: 'audit_app1_matrix',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'Marcel Lefter',
      action: 'app.role.updated',
      target: 'app_member',
      severity: 'medium',
      createdAt: minusDays(20)
    },
    {
      id: 'audit_app1_perm',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'Ana Moraru',
      action: 'app.permission.created',
      target: 'invoices.archive',
      severity: 'low',
      createdAt: minusDays(19)
    },
    {
      id: 'audit_app1_export',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_1',
      actor: 'system',
      action: 'app.credential.created',
      target: 'Reporting export key',
      severity: 'high',
      createdAt: minusDays(17)
    },
    {
      id: 'audit_app2_1',
      scope: 'app',
      workspaceId: 101,
      appId: 'app_2',
      actor: 'Marcel Lefter',
      action: 'app.credential.rotated',
      target: 'OAuth Client',
      severity: 'high',
      createdAt: minusDays(7)
    },
    {
      id: 'audit_app3_1',
      scope: 'app',
      workspaceId: 102,
      appId: 'app_3',
      actor: 'Marcel Lefter',
      action: 'app.webhook.deleted',
      target: 'legacy-hook',
      severity: 'medium',
      createdAt: minusDays(15)
    },
    {
      id: 'audit_app4_1',
      scope: 'app',
      workspaceId: 103,
      appId: 'app_4',
      actor: 'Elena Costa',
      action: 'app.credential.created',
      target: 'Partner integration',
      severity: 'high',
      createdAt: minusDays(15)
    },
    {
      id: 'audit_app5_1',
      scope: 'app',
      workspaceId: 103,
      appId: 'app_5',
      actor: 'Elena Costa',
      action: 'app.updated',
      target: 'Compliance Reporter',
      severity: 'low',
      createdAt: minusDays(5)
    }
  ]
})
